import * as React from 'react';
import { RedactedText } from '@/components/ui/citation-chip';
import { cn } from '@/lib/utils/cn';
import { kindLabel } from './kb-logic';

/**
 * Small presentational pieces of the vendor knowledge base screens, from
 * `docs/vendor-knowledge/prototype/knowledge.css` (and the shared
 * prototype tokens it builds on). Tokens only, through the Tailwind theme.
 */

// ---------------------------------------------------------------------------
// Chips (`.chip` and its tones)
// ---------------------------------------------------------------------------

export type ChipTone =
  | 'neutral'
  | 'healthy'
  | 'at-risk'
  | 'critical'
  | 'unassessed'
  | 'frozen'
  | 'amber'
  | 'ai';

const TONE: Record<ChipTone, string> = {
  neutral: 'border border-ink-100 bg-paper-100 text-ink-700',
  healthy: 'bg-posture-healthy-tint text-posture-healthy',
  'at-risk': 'bg-posture-at-risk-tint text-posture-at-risk',
  critical: 'bg-posture-critical-tint text-posture-critical',
  unassessed: 'bg-ink-100 text-posture-unassessed',
  frozen: 'bg-ink-100 text-ink-300',
  amber: 'bg-accent-amber-100 text-accent-amber-700',
  ai: 'border border-ai-surface-border bg-ai-surface text-ai-ink',
};

export function Chip({
  tone = 'neutral',
  size = 'sm',
  title,
  className,
  children,
}: {
  tone?: ChipTone;
  size?: 'sm' | 'md';
  title?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full font-medium',
        size === 'sm' ? 'h-5 px-2 text-[11px] leading-4' : 'h-[22px] px-2.5 text-meta',
        TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function KindChip({ kind }: { kind: string }) {
  return <Chip>{kindLabel(kind)}</Chip>;
}

/** An item or library number (`.mono-no`). */
export function MonoNo({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'whitespace-nowrap font-mono text-meta font-medium leading-[18px] text-ink-700',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Quotes (`.kb-x`): the source's own words. Never editable.
// ---------------------------------------------------------------------------

export type QuoteVariant = 'default' | 'lg' | 'clamp' | 'old';

export function Quote({
  text,
  variant = 'default',
  highlightRemoved = false,
  as: Tag = 'blockquote',
  className,
}: {
  text: string;
  variant?: QuoteVariant;
  highlightRemoved?: boolean;
  as?: 'blockquote' | 'div';
  className?: string;
}) {
  return (
    <Tag
      className={cn(
        'm-0 mt-2 block border-l-2 border-cite-chip-border py-0.5 pl-3.5 font-serif text-[15px] leading-6 text-ink-900',
        variant === 'lg' && 'mt-0.5 text-[16px] leading-[26px]',
        variant === 'clamp' && 'mt-1 line-clamp-2 text-[14px] leading-[22px] text-ink-500',
        variant === 'old' && 'text-ink-500 line-through decoration-ink-300 decoration-1',
        className,
      )}
    >
      “<RedactedText text={text} highlight={highlightRemoved} />”
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Sections (`.kb-sec`, `.kb-sec-head`, `.kb-sub`, `.kb-grp`)
// ---------------------------------------------------------------------------

export function Section({
  id,
  label,
  children,
  className,
}: {
  id?: string;
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      id={id}
      aria-label={label}
      className={cn('mt-8 border-t border-ink-100 pt-6', className)}
    >
      {children}
    </section>
  );
}

export function SectionHead({
  title,
  count,
  badge,
  hint,
  actions,
  as: Tag = 'h2',
}: {
  title: string;
  count?: number;
  badge?: React.ReactNode;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  as?: 'h2' | 'h3';
}) {
  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
      <Tag className="m-0 text-h2 font-semibold text-ink-900">{title}</Tag>
      {count !== undefined ? <Count>{count}</Count> : null}
      {badge}
      {hint ? <span className="text-meta text-ink-500">{hint}</span> : null}
      {actions ? <div className="ml-auto flex flex-wrap items-center gap-1">{actions}</div> : null}
    </div>
  );
}

/** A section's number (`.kb-n`). */
export function Count({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('font-mono text-meta tabular-nums text-ink-500', className)}>
      {typeof children === 'number' ? children.toLocaleString('en-US') : children}
    </span>
  );
}

export function SubHead({
  children,
  count,
  meta,
}: {
  children: React.ReactNode;
  count?: number;
  meta?: React.ReactNode;
}) {
  return (
    <div className="mb-2 mt-5 flex items-baseline gap-2 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-500">
      <span>{children}</span>
      {meta ? (
        <span className="text-meta font-normal normal-case tracking-normal">{meta}</span>
      ) : null}
      {count !== undefined ? <Count className="text-[11px]">{count}</Count> : null}
    </div>
  );
}

/** A review group (`.kb-grp`): its reason, count and one-line hint, then its cards. */
export function ReviewGroup({
  id,
  title,
  count,
  hint,
  children,
}: {
  id?: string;
  title: string;
  count?: number;
  hint: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div id={id} className="mt-5 first:mt-1" role="group" aria-label={title}>
      <div className="flex items-baseline gap-2 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-700">
        <span>{title}</span>
        {count !== undefined ? <Count className="text-[11px]">{count}</Count> : null}
      </div>
      <div className="mb-2.5 mt-0.5 text-meta text-ink-500">{hint}</div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

/** A dashed empty note (`.kb-empty`). */
export function Empty({
  children,
  small = false,
  className,
}: {
  children: React.ReactNode;
  small?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-card border border-dashed border-ink-200 text-ink-500',
        small ? 'px-3 py-2.5 text-meta' : 'px-4 py-[18px] text-body-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A small form opened in place (`.inline-form`). */
export function InlineForm({
  children,
  className,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <div
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
      className={cn(
        'mt-3 rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-paper-0 px-[18px] py-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A row of actions (`.kb-acts`). */
export function Actions({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn('mt-3 flex flex-wrap items-center gap-2', className)}>{children}</div>;
}

/** An underlined text button (`.linkish`). */
export const Linkish = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'default' | 'danger' | 'quiet' }
>(({ tone = 'default', className, type = 'button', ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(
      'inline-flex items-center gap-1 border-0 bg-transparent p-0 text-meta font-medium underline underline-offset-[3px]',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600',
      'disabled:cursor-not-allowed disabled:opacity-50',
      tone === 'danger' && 'text-posture-critical',
      tone === 'quiet' && 'text-ink-500 hover:text-ink-900',
      tone === 'default' && 'text-ink-700 hover:text-ink-900',
      className,
    )}
    {...props}
  />
));
Linkish.displayName = 'Linkish';

/** A note with an icon (`.kb-note`). */
export function Note({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-1.5 flex gap-1.5 text-meta text-ink-500">
      <span className="mt-[3px] flex-none" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** A tinted tip (`.tip`). */
export function Tip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-meta text-ink-700">
      <span className="mt-0.5 flex-none text-ink-500" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** A validation or server message under a form (`.kb-form-err`). */
export function FormError({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <div id={id} role="alert" className="mt-1.5 text-meta text-posture-critical">
      {children}
    </div>
  );
}

/** The labels of the prototype's forms. */
export function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-meta font-medium text-ink-700">
      {children}
    </label>
  );
}

export function FieldHelp({ children }: { children: React.ReactNode }) {
  return <div className="mt-1.5 text-meta text-ink-500">{children}</div>;
}

/** `.input.sel-sm`: the 30px control used in rows and in-place forms. */
export const smallControl =
  'h-[30px] rounded-card border border-ink-200 bg-paper-0 px-2.5 text-meta text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

/** `.input` for in-place textareas. */
export const textareaControl =
  'block w-full rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

/** A label and value row of a facts list (`.kb-facts`). */
export function Facts({ children }: { children: React.ReactNode }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-[168px_minmax(0,1fr)]">
      {children}
    </dl>
  );
}

export function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-meta font-medium leading-[22px] text-ink-500">{label}</dt>
      <dd className="m-0 flex min-h-[22px] flex-wrap items-center gap-1.5">{children}</dd>
    </>
  );
}

/** A small rounded chip in a facts row (`.dchip`). */
export function DChip({
  children,
  title,
  sub,
}: {
  children: React.ReactNode;
  title?: string;
  sub?: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className="inline-flex h-[22px] items-center gap-1 rounded-full border border-ink-100 bg-paper-100 px-2 text-meta font-medium text-ink-700"
    >
      {children}
      {sub ? <span className="ml-1 font-normal text-ink-500">{sub}</span> : null}
    </span>
  );
}

/** The dashed "+ Add …" chip (`.dchip.add`). */
export const DChipAdd = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, type = 'button', children, ...props }, ref) => (
  <button
    ref={ref}
    type={type}
    className={cn(
      'inline-flex h-[22px] items-center gap-1 rounded-full border border-dashed border-ink-100 bg-paper-0 px-2 text-meta font-medium text-ink-500',
      'hover:border-ink-300 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600',
      className,
    )}
    {...props}
  >
    {children}
  </button>
));
DChipAdd.displayName = 'DChipAdd';

/** The grey summary line under a header (`.kb-summary`): parts separated by dots. */
export function SummaryLine({ parts }: { parts: React.ReactNode[] }) {
  return (
    <div className="mt-[22px] flex flex-wrap rounded-card border border-ink-100 bg-paper-50 px-4 py-[11px] text-body-sm text-ink-700">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center">
          {p}
          {i < parts.length - 1 ? (
            <span aria-hidden="true" className="mx-2.5 text-ink-300">
              ·
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

/** A bold number inside a summary line. */
export function B({ children }: { children: React.ReactNode }) {
  return <b className="font-semibold tabular-nums text-ink-900">{children}</b>;
}

/** A page's outer frame on the right (`.kb-page`). */
export function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('max-w-[1000px] px-4 pb-[72px] pt-7 sm:px-8', className)}>{children}</div>
  );
}

/** A page heading with its one-line purpose (`.kb-home-head`). */
export function PageHead({
  title,
  eyebrow,
  children,
  aside,
}: {
  title: string;
  eyebrow?: string;
  children?: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <div>
      {eyebrow ? (
        <div className="mb-1 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-500">
          {eyebrow}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="m-0 text-h1 font-semibold text-ink-900">{title}</h1>
        {aside}
      </div>
      {children ? (
        <p className="mb-0 mt-1 max-w-[640px] text-body text-ink-500">{children}</p>
      ) : null}
    </div>
  );
}
